import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Calculator,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Copy,
  Eye,
  Loader2,
  LogIn,
  Maximize2,
  Minimize2,
  Pause,
  RefreshCw,
  Search,
  ShieldCheck,
  Users,
  Wallet,
  X,
  Zap,
} from "lucide-react";
import { fetchAdminActivityOverview, prepareAdminReplayFromActivityEvent } from "../api.js";
import { openAdminReplayAnalysis } from "../utils/adminReplayLaunch.js";

const LIVE_REFRESH_MS = 15_000;

const RANGE_PRESETS = [
  { key: "24h", days: 1, granularity: "hour", label: "24h" },
  { key: "7d", days: 7, granularity: "day", label: "7 Tage" },
  { key: "30d", days: 30, granularity: "day", label: "30 Tage" },
  { key: "90d", days: 90, granularity: "day", label: "90 Tage" },
];

const FILTER_CHIPS = [
  ["all", "Alle"],
  ["login", "Login"],
  ["preview", "Preview"],
  ["calc", "Calc"],
  ["failed", "Fehler"],
];

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function shortWallet(wallet = "") {
  const w = String(wallet || "");
  if (!w) return "—";
  return `${w.slice(0, 10)}…${w.slice(-8)}`;
}

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function formatRelative(value) {
  if (!value) return "";
  const ts = new Date(value).getTime();
  if (!Number.isFinite(ts)) return "";
  const diffSec = Math.round((Date.now() - ts) / 1000);
  if (diffSec < 10) return "gerade eben";
  if (diffSec < 60) return `vor ${diffSec}s`;
  if (diffSec < 3600) return `vor ${Math.floor(diffSec / 60)} Min`;
  if (diffSec < 86400) return `vor ${Math.floor(diffSec / 3600)} Std`;
  return formatDate(value);
}

function eventMeta(eventType) {
  const t = String(eventType || "").toLowerCase();
  if (t === "login") return { tone: "slate", Icon: LogIn };
  if (t.endsWith("_failed") || t.endsWith("_blocked")) return { tone: "red", Icon: AlertTriangle };
  if (t.endsWith("_completed")) return { tone: "emerald", Icon: CheckCircle2 };
  if (t.endsWith("_started")) return { tone: "blue", Icon: Activity };
  if (t.includes("preview")) return { tone: "blue", Icon: Eye };
  if (t.includes("calculation")) return { tone: "emerald", Icon: Calculator };
  return { tone: "slate", Icon: Activity };
}

const toneStyles = {
  slate: "bg-slate-100 text-slate-700 ring-slate-200",
  blue: "bg-blue-50 text-blue-700 ring-blue-100",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-100",
  red: "bg-red-50 text-red-700 ring-red-100",
};

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {}
}

function filterEventType(eventType, filter) {
  const t = String(eventType || "").toLowerCase();
  if (filter === "all") return true;
  if (filter === "login") return t === "login";
  if (filter === "preview") return t.includes("preview");
  if (filter === "calc") return t.includes("calculation");
  if (filter === "failed") return t.endsWith("_failed") || t.endsWith("_blocked");
  return true;
}

function buildQueryOptions({ preset, walletSearch }) {
  const opts = { days: preset.days, granularity: preset.granularity, limit: 1000 };

  if (preset.key === "24h") {
    const to = new Date();
    const from = new Date(to.getTime() - 24 * 3600000);
    opts.from = from.toISOString();
    opts.to = to.toISOString();
    opts.days = 1;
  }

  const walletQ = String(walletSearch || "").trim();
  if (walletQ) opts.wallet = walletQ;
  return opts;
}

const FILTER_LABELS = {
  all: "Alle Aktivitäten",
  login: "Logins",
  preview: "Previews",
  calc: "Berechnungen",
  failed: "Fehler",
};

function WalletActivitySummary({ sessions, eventFilter, onSelectWallet }) {
  if (!sessions.length) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white px-5 py-8 text-center text-sm text-slate-500">
        Keine Wallets für diesen Filter im Zeitraum.
      </div>
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {sessions.map((s) => (
        <button
          key={s.user_wallet}
          type="button"
          onClick={() => onSelectWallet(s.user_wallet)}
          className="rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-blue-400 hover:bg-blue-50/30 hover:shadow-md"
        >
          <div className="mb-2 font-mono text-xs font-bold text-slate-900 break-all">{s.user_wallet}</div>
          <div className="mb-2 text-xs text-slate-500">Zuletzt: {formatRelative(s.last_seen_at)}</div>
          <div className="flex flex-wrap gap-1.5">
            {s.login_count > 0 ? (
              <span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-700">
                {s.login_count}× Login
              </span>
            ) : null}
            {s.preview_count > 0 ? (
              <span className="rounded-md bg-blue-50 px-2 py-0.5 text-xs font-bold text-blue-700">
                {s.preview_count}× Preview
              </span>
            ) : null}
            {s.calculation_count > 0 ? (
              <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-bold text-emerald-700">
                {s.calculation_count}× Calc
              </span>
            ) : null}
            {s.failure_count > 0 ? (
              <span className="rounded-md bg-red-50 px-2 py-0.5 text-xs font-bold text-red-700">
                {s.failure_count}× Fehler
              </span>
            ) : null}
          </div>
          {s.analyzed_wallets?.length ? (
            <div className="mt-2 text-xs text-slate-600">
              <span className="font-semibold">{s.analyzed_wallets.length}</span> analysierte VeChain-Wallet(s)
            </div>
          ) : null}
          {s.last_visitor_country ? (
            <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
              <span>Herkunft:</span>
              <CountryBadge code={s.last_visitor_country} />
            </div>
          ) : null}
        </button>
      ))}
    </div>
  );
}

function KpiFilter({ label, value, sub, accent = "slate", active = false, onClick, disabled = false }) {
  const labelColors = {
    blue: "text-blue-700",
    emerald: "text-emerald-700",
    red: "text-red-700",
    slate: "text-slate-600",
    violet: "text-violet-700",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "w-full rounded-xl border px-4 py-3 text-left shadow-sm transition focus:outline-none focus:ring-2 focus:ring-blue-300",
        active
          ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200"
          : "border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/40",
        disabled && "cursor-not-allowed opacity-40 hover:border-slate-200 hover:bg-white"
      )}
    >
      <div className={cn("mb-1 flex items-center justify-between text-[10px] font-bold uppercase tracking-wider", labelColors[accent] || labelColors.slate)}>
        <span>{label}</span>
        {active ? <span className="rounded bg-blue-600 px-1.5 py-0.5 text-[9px] text-white">aktiv</span> : null}
      </div>
      <div className="text-3xl font-bold tabular-nums text-slate-950">
        {Number(value || 0).toLocaleString("de-DE")}
      </div>
      {sub ? <div className="mt-0.5 text-xs text-slate-500">{sub}</div> : null}
    </button>
  );
}

function FunnelBar({ label, value, max, color, active = false, onClick }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  const inner = (
    <>
      <div className="mb-1 flex justify-between text-xs">
        <span className={cn("font-medium", active ? "text-blue-800 font-bold" : "text-slate-700")}>{label}</span>
        <span className="font-bold tabular-nums text-slate-900">{value}</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
        <div className={cn("h-full rounded-full", color)} style={{ width: `${Math.max(pct, value > 0 ? 4 : 0)}%` }} />
      </div>
    </>
  );
  if (!onClick) return <div>{inner}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full rounded-lg px-2 py-1.5 text-left transition hover:bg-slate-50",
        active && "bg-blue-50 ring-1 ring-blue-200"
      )}
    >
      {inner}
    </button>
  );
}

function bucketKey(date, granularity = "day") {
  const d = new Date(date);
  if (granularity === "hour") {
    d.setMinutes(0, 0, 0);
    return d.toISOString();
  }
  return d.toISOString().slice(0, 10);
}

function bucketLabel(key, granularity = "day") {
  const d = new Date(granularity === "hour" ? key : `${key}T12:00:00`);
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startYesterday = new Date(startToday.getTime() - 86400000);
  const startBucket = new Date(granularity === "hour" ? key : `${key}T00:00:00`);
  if (startBucket >= startToday) return "Heute";
  if (startBucket >= startYesterday) return "Gestern";
  return d.toLocaleDateString("de-DE", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: granularity === "hour" ? undefined : "2-digit",
    ...(granularity === "hour" ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
}

function CountryBadge({ code, title }) {
  if (!code) return null;
  return (
    <span
      className="inline-flex items-center rounded-lg bg-violet-50 px-2.5 py-1 text-sm font-bold tracking-wide text-violet-800 ring-1 ring-violet-100"
      title={title || `Herkunftsland: ${code}`}
    >
      {code}
    </span>
  );
}

function matchesExtraFilters(event, { sourceFilter, countryFilter }) {
  if (sourceFilter && String(event.source || "").toLowerCase() !== sourceFilter.toLowerCase()) {
    return false;
  }
  if (countryFilter && String(event.visitor_country || "").toUpperCase() !== countryFilter.toUpperCase()) {
    return false;
  }
  return true;
}

function isReplayableEvent(event) {
  const t = String(event?.event_type || "").toLowerCase();
  if (!t.includes("calculation") && !t.includes("preview")) return false;
  if (!event?.tax_year) return false;
  if (!event?.dataset_id && !(event?.analyzed_wallets || []).length) return false;
  return true;
}

function EventRow({ event, expanded, onToggle, onReplay, replayLoading }) {
  const m = eventMeta(event.event_type);
  const Icon = m.Icon;
  const wallets = event.analyzed_wallets || [];

  return (
    <div className="border-b border-slate-100 px-5 py-4 last:border-0 hover:bg-slate-50/70">
      <div className="flex items-start gap-4">
        <span className={cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ring-1", toneStyles[m.tone])}>
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="grid gap-3 lg:grid-cols-[1fr_auto] lg:items-start">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <div className="text-base font-bold text-slate-900">{event.label || event.event_type}</div>
                <CountryBadge code={event.visitor_country} title="Herkunftsland (IP)" />
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="rounded-lg bg-slate-100 px-2.5 py-1 font-mono text-sm text-slate-800" title={event.user_wallet}>
                  {shortWallet(event.user_wallet)}
                </span>
                <button type="button" onClick={() => copyText(event.user_wallet)} className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50" title="Nutzer-Wallet kopieren">
                  <Copy className="mr-1 inline h-3.5 w-3.5" />
                  Kopieren
                </button>
              </div>
            </div>
            <div className="shrink-0 rounded-lg bg-slate-50 px-3 py-2 text-right text-sm text-slate-600">
              <div className="font-bold text-slate-900">{formatRelative(event.created_at)}</div>
              <div className="text-xs">{formatDate(event.created_at)}</div>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {event.source ? <span className="rounded-lg bg-slate-100 px-2.5 py-1 text-sm text-slate-700">{event.source}</span> : null}
            {event.selected_country ? (
              <span className="rounded-lg bg-slate-100 px-2.5 py-1 text-sm text-slate-700" title="Gewählte Steuerjurisdiktion">
                Steuer {event.selected_country}
              </span>
            ) : null}
            {event.tax_year ? <span className="rounded-lg bg-slate-100 px-2.5 py-1 text-sm text-slate-700">Jahr {event.tax_year}</span> : null}
            {event.dataset_id ? <span className="rounded-lg bg-indigo-50 px-2.5 py-1 text-sm font-semibold text-indigo-700">Dataset #{event.dataset_id}</span> : null}
            {event.wallet_count > 0 ? (
              <button type="button" onClick={onToggle} className="inline-flex items-center gap-1.5 rounded-lg bg-blue-50 px-2.5 py-1 text-sm font-semibold text-blue-800 hover:bg-blue-100">
                {event.wallet_count} analysierte Wallet{event.wallet_count !== 1 ? "s" : ""}
                {wallets.length ? expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" /> : null}
              </button>
            ) : null}
            {isReplayableEvent(event) ? (
              <button
                type="button"
                disabled={replayLoading}
                onClick={() => onReplay?.(event)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-2.5 py-1 text-sm font-semibold text-emerald-800 ring-1 ring-emerald-100 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                title="Admin-Nachrechnung starten (überschreibt keine User-Snapshots)"
              >
                {replayLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />}
                Nachrechnen
              </button>
            ) : null}
          </div>
          {expanded && wallets.length ? (
            <ul className="mt-3 max-h-52 space-y-1.5 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-3">
              {wallets.map((w) => (
                <li key={w} className="flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2 font-mono text-sm text-slate-800">
                  <span className="truncate">{w}</span>
                  <button type="button" onClick={() => copyText(w)} className="shrink-0 rounded border border-slate-200 px-2 py-0.5 text-xs text-slate-600 hover:bg-slate-50">
                    Kopieren
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function AdminActivityDashboard({ isAdmin = false }) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [tab, setTab] = useState("timeline");
  const [presetKey, setPresetKey] = useState("7d");
  const [eventFilter, setEventFilter] = useState("all");
  const [walletSearch, setWalletSearch] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [liveEnabled, setLiveEnabled] = useState(true);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [chartsOpen, setChartsOpen] = useState(false);
  const [expandedEvents, setExpandedEvents] = useState({});
  const [selectedWallet, setSelectedWallet] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [replayEventId, setReplayEventId] = useState(null);
  const [replayErr, setReplayErr] = useState("");
  const loadRef = useRef(null);

  const applyEventFilter = useCallback((filterKey) => {
    setEventFilter((prev) => (prev === filterKey ? "all" : filterKey));
    setTab(filterKey === "all" ? "timeline" : "wallets");
  }, []);

  const selectWallet = useCallback((wallet) => {
    setSelectedWallet(wallet);
    setTab("timeline");
  }, []);

  const resetAllFilters = useCallback(() => {
    setEventFilter("all");
    setSourceFilter("");
    setCountryFilter("");
    setSelectedWallet("");
    setWalletSearch("");
  }, []);

  const handleAdminReplay = useCallback(async (event) => {
    if (!event?.id) return;
    setReplayErr("");
    setReplayEventId(event.id);
    try {
      const prepared = await prepareAdminReplayFromActivityEvent(event);
      openAdminReplayAnalysis(prepared);
    } catch (error) {
      const message =
        error?.body?.error ||
        error?.message ||
        "Admin-Nachrechnung konnte nicht vorbereitet werden.";
      setReplayErr(message);
    } finally {
      setReplayEventId(null);
    }
  }, []);

  const preset = RANGE_PRESETS.find((p) => p.key === presetKey) || RANGE_PRESETS[1];

  const load = useCallback(async (silent = false) => {
    if (!isAdmin) return;
    if (!silent) setLoading(true);
    setErr("");
    try {
      const opts = buildQueryOptions({
        preset,
        walletSearch: selectedWallet || walletSearch,
      });
      const json = await fetchAdminActivityOverview(opts);
      setData(json);
      setLastFetchedAt(json.fetchedAt || new Date().toISOString());
    } catch (error) {
      if (error?.status === 403) {
        setErr("Nur die Admin-Wallet hat Zugriff auf dieses Dashboard.");
      } else {
        setErr(error?.message || "Admin-Daten konnten nicht geladen werden.");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [isAdmin, preset]);

  loadRef.current = load;

  const rawEvents = useMemo(() => data?.recentEvents || [], [data]);

  const filteredEvents = useMemo(() => {
    const q = String(selectedWallet || walletSearch || "").trim().toLowerCase();
    return rawEvents.filter((e) => {
      if (!filterEventType(e.event_type, eventFilter)) return false;
      if (!matchesExtraFilters(e, { sourceFilter, countryFilter })) return false;
      if (q && !String(e.user_wallet || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rawEvents, eventFilter, sourceFilter, countryFilter, selectedWallet, walletSearch]);

  const filteredStats = useMemo(() => {
    const loginEv = filteredEvents.filter((e) => e.event_type === "login");
    const previewEv = filteredEvents.filter((e) => String(e.event_type || "").includes("preview"));
    const calcEv = filteredEvents.filter((e) => String(e.event_type || "").includes("calculation"));
    const failEv = filteredEvents.filter((e) => {
      const t = String(e.event_type || "");
      return t.endsWith("_failed") || t.endsWith("_blocked");
    });
    return {
      total: filteredEvents.length,
      users: new Set(filteredEvents.map((e) => e.user_wallet)).size,
      logins: loginEv.length,
      previews: previewEv.filter((e) => String(e.event_type || "").endsWith("_started")).length,
      previewDone: previewEv.filter((e) => String(e.event_type || "").endsWith("_completed")).length,
      calcs: calcEv.filter((e) => String(e.event_type || "").endsWith("_started")).length,
      calcDone: calcEv.filter((e) => String(e.event_type || "").endsWith("_completed")).length,
      failures: failEv.length,
      loginWallets: loginEv.map((e) => e.user_wallet),
    };
  }, [filteredEvents]);

  useEffect(() => {
    if (open) load(false);
  }, [open, load]);

  useEffect(() => {
    if (!open || !liveEnabled || !isAdmin) return undefined;
    const id = setInterval(() => loadRef.current?.(true), LIVE_REFRESH_MS);
    return () => clearInterval(id);
  }, [open, liveEnabled, isAdmin]);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("v3tax:open-admin-dashboard", onOpen);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("v3tax:open-admin-dashboard", onOpen);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  const timeline = useMemo(() => {
    const buckets = new Map();
    for (const ev of filteredEvents) {
      const key = bucketKey(ev.created_at, data?.range?.granularity === "hour" ? "hour" : "day");
      if (!buckets.has(key)) {
        buckets.set(key, {
          bucket: key,
          bucketLabel: bucketLabel(key, data?.range?.granularity === "hour" ? "hour" : "day"),
          unique_users: new Set(),
          login_count: 0,
          events: [],
        });
      }
      const b = buckets.get(key);
      b.events.push(ev);
      b.unique_users.add(ev.user_wallet);
      if (ev.event_type === "login") b.login_count += 1;
    }
    return [...buckets.values()]
      .sort((a, b) => new Date(b.bucket) - new Date(a.bucket))
      .map((b) => ({
        ...b,
        unique_users: b.unique_users.size,
        events: b.events.sort((x, y) => new Date(y.created_at) - new Date(x.created_at)),
      }));
  }, [filteredEvents, data?.range?.granularity]);

  const sessions = useMemo(() => {
    const byUser = new Map();
    for (const ev of filteredEvents) {
      const w = ev.user_wallet;
      if (!byUser.has(w)) {
        byUser.set(w, {
          user_wallet: w,
          last_seen_at: ev.created_at,
          first_seen_at: ev.created_at,
          login_count: 0,
          preview_count: 0,
          calculation_count: 0,
          failure_count: 0,
          analyzed_wallets: new Set(),
          last_visitor_country: null,
          last_country: null,
          events: [],
        });
      }
      const s = byUser.get(w);
      if (new Date(ev.created_at) > new Date(s.last_seen_at)) s.last_seen_at = ev.created_at;
      if (ev.event_type === "login") s.login_count += 1;
      if (String(ev.event_type || "").includes("preview")) s.preview_count += 1;
      if (String(ev.event_type || "").includes("calculation")) s.calculation_count += 1;
      if (String(ev.event_type || "").endsWith("_failed") || String(ev.event_type || "").endsWith("_blocked")) s.failure_count += 1;
      if (ev.visitor_country) s.last_visitor_country = ev.visitor_country;
      if (ev.selected_country) s.last_country = ev.selected_country;
      for (const aw of ev.analyzed_wallets || []) s.analyzed_wallets.add(aw);
      s.events.push(ev);
    }
    let list = [...byUser.values()]
      .sort((a, b) => new Date(b.last_seen_at) - new Date(a.last_seen_at))
      .map((s) => ({
        ...s,
        analyzed_wallets: [...s.analyzed_wallets],
      }));

    const q = String(selectedWallet || walletSearch || "").trim().toLowerCase();
    if (q) list = list.filter((s) => String(s.user_wallet || "").includes(q));
    if (eventFilter === "login") list = list.filter((s) => s.login_count > 0);
    if (eventFilter === "preview") list = list.filter((s) => s.preview_count > 0);
    if (eventFilter === "calc") list = list.filter((s) => s.calculation_count > 0);
    if (eventFilter === "failed") list = list.filter((s) => s.failure_count > 0);
    return list;
  }, [filteredEvents, walletSearch, selectedWallet, eventFilter]);

  const hasActiveFilters =
    eventFilter !== "all" || sourceFilter || countryFilter || selectedWallet || String(walletSearch || "").trim();

  const summary = data?.summary || {};
  const insights = data?.insights || {};
  const funnel = insights.funnel || summary;
  const meta = data?.meta || {};
  const hasEvents = Number(summary.total_events || 0) > 0;
  const funnelMax = Math.max(
    1,
    funnel.logins || 0,
    funnel.preview_started || 0,
    funnel.preview_completed || 0,
    funnel.calculations_started || 0
  );

  const panelClass = expanded
    ? "w-[min(96vw,1280px)] max-h-[90vh]"
    : "w-[min(92vw,1040px)] max-h-[85vh]";

  if (!isAdmin) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-[250] inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-white/95 px-3 py-2 text-sm font-semibold text-slate-900 shadow-lg backdrop-blur hover:border-blue-300"
      >
        <span className="relative flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600 text-white">
          <ShieldCheck className="h-4 w-4" />
          {Number(meta.events_last_24h || 0) > 0 ? (
            <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white" />
          ) : null}
        </span>
        <span className="hidden sm:inline">Admin Live</span>
      </button>

      {open ? (
        <div
          className="fixed inset-0 z-[260] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-[2px] sm:p-6"
          onClick={() => setOpen(false)}
          role="presentation"
        >
          <div
            className={cn(
              "flex shrink-0 flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_25px_80px_rgba(15,23,42,0.25)]",
              panelClass
            )}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-activity-title"
          >
            <div className="shrink-0 border-b border-slate-200 bg-white px-5 py-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-md">
                    <BarChart3 className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 id="admin-activity-title" className="text-lg font-bold text-slate-950">
                      Platform Activity
                    </h2>
                    <p className="text-sm text-slate-500">
                      Aktivitätsmessung V3Tax · Logins · Preview · Berechnungen · Wallets
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-bold text-blue-800">
                    24h: {meta.events_last_24h ?? 0} Events · {meta.users_last_24h ?? 0} Nutzer
                  </span>
                  <button type="button" onClick={() => setLiveEnabled((v) => !v)} className={cn("inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-sm font-semibold", liveEnabled ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-white text-slate-600")}>
                    {liveEnabled ? <Zap className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
                    {liveEnabled ? "Live" : "Pause"}
                  </button>
                  {RANGE_PRESETS.map((p) => (
                    <button key={p.key} type="button" onClick={() => { setPresetKey(p.key); setSelectedWallet(""); }} className={cn("h-9 rounded-lg px-3 text-sm font-semibold", presetKey === p.key ? "bg-blue-600 text-white shadow-sm" : "bg-slate-100 text-slate-700 hover:bg-slate-200")}>
                      {p.label}
                    </button>
                  ))}
                  <button type="button" onClick={() => load(false)} disabled={loading} className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white hover:bg-slate-50">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  </button>
                  <button type="button" onClick={() => setExpanded((v) => !v)} className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white hover:bg-slate-50" title={expanded ? "Verkleinern" : "Vergrößern"}>
                    {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                  </button>
                  <button type="button" onClick={() => setOpen(false)} className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white hover:bg-slate-50" aria-label="Schließen">
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
              {lastFetchedAt ? (
                <p className="mt-2 text-xs text-slate-500">
                  Aktualisiert: {formatDate(lastFetchedAt)}
                  {meta.tracking_since ? ` · Tracking seit ${formatDate(meta.tracking_since)}` : ""}
                  {meta.last_event_at ? ` · Letztes Event: ${formatRelative(meta.last_event_at)}` : ""}
                  {meta.events_truncated ? (
                    <span className="ml-1 font-semibold text-amber-700">
                      · Anzeige: {meta.events_loaded} von {meta.events_total_in_range} Events (neueste zuerst)
                    </span>
                  ) : null}
                </p>
              ) : null}
            </div>

            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
              <div className="w-full px-5 py-4">
              {err ? (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                  {err}
                  <button type="button" onClick={() => load(false)} className="ml-2 underline">Erneut laden</button>
                </div>
              ) : null}

              {replayErr ? (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                  {replayErr}
                </div>
              ) : null}

              {hasActiveFilters ? (
                <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm text-blue-900">
                  <span className="font-bold">Aktive Filter:</span>
                  {eventFilter !== "all" ? <span className="rounded-lg bg-white px-2 py-0.5 font-semibold">{FILTER_CHIPS.find(([k]) => k === eventFilter)?.[1] || eventFilter}</span> : null}
                  {sourceFilter ? <span className="rounded-lg bg-white px-2 py-0.5 font-semibold">Quelle: {sourceFilter}</span> : null}
                  {countryFilter ? <span className="rounded-lg bg-white px-2 py-0.5 font-semibold">Herkunft: {countryFilter}</span> : null}
                  {(selectedWallet || String(walletSearch || "").trim()) ? (
                    <span className="rounded-lg bg-white px-2 py-0.5 font-mono font-semibold">{shortWallet(selectedWallet || walletSearch)}</span>
                  ) : null}
                  <span className="text-blue-700">→ {filteredStats.total} Events · {filteredStats.users} Nutzer</span>
                  <button type="button" onClick={resetAllFilters} className="ml-auto font-bold underline hover:text-blue-950">
                    Alle Filter zurücksetzen
                  </button>
                </div>
              ) : null}

              <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                <KpiFilter
                  label="Events"
                  value={hasActiveFilters ? filteredStats.total : summary.total_events}
                  accent="blue"
                  active={eventFilter === "all" && !sourceFilter && !countryFilter}
                  onClick={() => { setEventFilter("all"); setSourceFilter(""); setCountryFilter(""); setTab("timeline"); }}
                />
                <KpiFilter
                  label="Nutzer"
                  value={hasActiveFilters ? filteredStats.users : summary.unique_users}
                  accent="slate"
                  active={(tab === "wallets" || tab === "users") && eventFilter === "all"}
                  onClick={() => { setEventFilter("all"); setTab("wallets"); }}
                />
                <KpiFilter
                  label="Logins"
                  value={hasActiveFilters ? filteredStats.logins : summary.logins}
                  accent="slate"
                  active={eventFilter === "login"}
                  onClick={() => applyEventFilter("login")}
                />
                <KpiFilter
                  label="Previews"
                  value={hasActiveFilters ? filteredStats.previews : summary.preview_started}
                  sub={`${hasActiveFilters ? filteredStats.previewDone : summary.preview_completed || 0} fertig`}
                  accent="blue"
                  active={eventFilter === "preview"}
                  onClick={() => applyEventFilter("preview")}
                />
                <KpiFilter
                  label="Berechnungen"
                  value={hasActiveFilters ? filteredStats.calcs : summary.calculations_started}
                  sub={`${hasActiveFilters ? filteredStats.calcDone : summary.calculations_completed || 0} fertig`}
                  accent="emerald"
                  active={eventFilter === "calc"}
                  onClick={() => applyEventFilter("calc")}
                />
                <KpiFilter
                  label="Fehler"
                  value={hasActiveFilters ? filteredStats.failures : summary.failures}
                  accent="red"
                  active={eventFilter === "failed"}
                  onClick={() => applyEventFilter("failed")}
                />
              </div>

              {!hasEvents && !loading ? (
                <p className="mb-4 rounded-lg border border-dashed border-slate-300 bg-white px-4 py-3 text-sm text-slate-600">
                  Noch keine Events im gewählten Zeitraum. Nach Login, Preview oder Berechnung erscheinen hier alle Details inkl. analysierter Wallets.
                </p>
              ) : null}

              {Number(summary.logins || 0) > 0 &&
              Number(summary.preview_started || 0) === 0 &&
              Number(summary.calculations_started || 0) === 0 ? (
                <p className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-700">
                  Es wurden Logins erfasst, aber noch keine Previews/Berechnungen im gewählten Zeitraum. Berechnungen werden serverseitig beim Start von{" "}
                  <code className="rounded bg-white px-1">/calculate/start</code> geloggt — nicht bei Sync oder Snapshot-Anzeige. Test-/Betreiber-Wallets sind ausgeschlossen.
                </p>
              ) : null}

              {Number(summary.logins || 0) === 0 && Number(summary.preview_started || 0) > 0 ? (
                <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">
                  Hinweis: Previews ohne Login-Zähler — Nutzer war vermutlich schon eingeloggt (Session). Ab jetzt wird bei aktiver Session max. alle 12h ein Login-Event erfasst.
                </p>
              ) : null}

              <div className="mb-4 flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50/80 p-3 sm:flex-row sm:items-center">
                <div className="relative min-w-0 flex-1">
                  <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <input
                    value={walletSearch}
                    onChange={(e) => { setWalletSearch(e.target.value); setSelectedWallet(""); }}
                    placeholder="Wallet suchen…"
                    className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-10 pr-3 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {FILTER_CHIPS.map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        if (key === "all") {
                          setEventFilter("all");
                          setTab("timeline");
                        } else {
                          applyEventFilter(key);
                        }
                      }}
                      className={cn(
                        "rounded-lg px-3 py-1.5 text-xs font-bold",
                        eventFilter === key ? "bg-blue-600 text-white" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <details className="mb-4 rounded-xl border border-slate-200 bg-slate-50/50">
                <summary className="cursor-pointer px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-slate-500">
                  Insights (Trichter · Quellen · Herkunftsländer)
                </summary>
                <div className="grid gap-3 border-t border-slate-200 p-3 sm:grid-cols-3">
                  <div className="space-y-2">
                    <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Quellen</div>
                    {(insights.sources || []).map((r) => (
                      <button
                        key={r.source}
                        type="button"
                        onClick={() => {
                          setSourceFilter((prev) => (prev === r.source ? "" : r.source));
                          setTab("wallets");
                        }}
                        className={cn(
                          "flex w-full justify-between rounded-lg px-2 py-1 text-sm",
                          sourceFilter === r.source ? "bg-blue-100 font-semibold text-blue-800" : "hover:bg-white"
                        )}
                      >
                        <span>{r.source}</span>
                        <span>{r.count}</span>
                      </button>
                    ))}
                  </div>
                  <div className="space-y-2">
                    <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Herkunft (Logins)</div>
                    {(insights.loginCountries || insights.visitorCountries || []).map((r) => (
                      <button
                        key={`login-${r.country}`}
                        type="button"
                        onClick={() => {
                          if (r.country === "—") return;
                          setCountryFilter((prev) => (prev === r.country ? "" : r.country));
                          setEventFilter("login");
                          setTab("timeline");
                        }}
                        className={cn(
                          "flex w-full justify-between rounded-lg px-2 py-1 text-sm",
                          countryFilter === r.country ? "bg-violet-100 font-semibold text-violet-800" : "hover:bg-white"
                        )}
                      >
                        <span>{r.country === "—" ? "Unbekannt" : r.country}</span>
                        <span>{r.count}</span>
                      </button>
                    ))}
                  </div>
                  <div className="space-y-2">
                    <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Trichter</div>
                    <FunnelBar label="Logins" value={funnel.logins || 0} max={funnelMax} color="bg-slate-500" active={eventFilter === "login"} onClick={() => applyEventFilter("login")} />
                    <FunnelBar label="Previews" value={funnel.preview_started || 0} max={funnelMax} color="bg-blue-500" active={eventFilter === "preview"} onClick={() => applyEventFilter("preview")} />
                    <FunnelBar label="Berechnungen" value={funnel.calculations_started || 0} max={funnelMax} color="bg-emerald-500" active={eventFilter === "calc"} onClick={() => applyEventFilter("calc")} />
                  </div>
                </div>
              </details>

              {selectedWallet ? (
                <div className="mb-3 flex items-center gap-2 rounded-lg bg-blue-100 px-3 py-2 text-sm text-blue-900">
                  <Users className="h-4 w-4" />
                  Wallet-Filter: <span className="font-mono font-bold">{selectedWallet}</span>
                  <button type="button" onClick={() => setSelectedWallet("")} className="ml-auto font-bold underline">zurücksetzen</button>
                </div>
              ) : null}

              <div className="flex min-h-[360px] flex-col rounded-xl border border-slate-200 bg-white shadow-sm">
                  <div className="flex shrink-0 border-b border-slate-200">
                    {[["timeline", "Timeline — Detail-Events"], ["wallets", "Wallet-Übersicht"], ["users", "Tabelle"]].map(([key, label]) => (
                      <button key={key} type="button" onClick={() => setTab(key)} className={cn("flex-1 px-5 py-3.5 text-sm font-bold", tab === key ? "border-b-2 border-blue-600 text-blue-700 bg-blue-50/50" : "text-slate-500 hover:text-slate-800")}>
                        {label}
                      </button>
                    ))}
                  </div>

                  <div className="min-h-0 flex-1 overflow-y-auto">
                    {loading && !data ? (
                      <div className="flex items-center justify-center py-20 text-slate-500">
                        <Loader2 className="mr-2 h-6 w-6 animate-spin" /> Lädt Aktivitätsdaten…
                      </div>
                    ) : tab === "wallets" ? (
                      <div className="p-4">
                        <WalletActivitySummary
                          sessions={sessions}
                          eventFilter={eventFilter}
                          onSelectWallet={selectWallet}
                        />
                      </div>
                    ) : tab === "timeline" ? (
                      timeline.length ? (
                        timeline.map((bucket) => (
                          <div key={bucket.bucket}>
                            <div className="sticky top-0 z-10 border-b border-slate-200 bg-slate-100 px-5 py-2.5 text-sm font-bold uppercase tracking-wider text-slate-700">
                              {bucket.bucketLabel}
                              <span className="ml-2 font-normal normal-case text-slate-500">
                                · {bucket.events.length} Events · {bucket.unique_users} Nutzer
                                {bucket.login_count > 0 ? ` · ${bucket.login_count} Logins` : ""}
                              </span>
                            </div>
                            {bucket.events.map((ev) => (
                              <EventRow
                                key={ev.id}
                                event={ev}
                                expanded={!!expandedEvents[ev.id]}
                                onToggle={() => setExpandedEvents((s) => ({ ...s, [ev.id]: !s[ev.id] }))}
                                onReplay={handleAdminReplay}
                                replayLoading={replayEventId === ev.id}
                              />
                            ))}
                          </div>
                        ))
                      ) : (
                        <p className="p-12 text-center text-sm text-slate-500">Keine Events für diesen Filter</p>
                      )
                    ) : (
                      <table className="min-w-full text-base">
                        <thead className="sticky top-0 z-10 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                          <tr>
                            <th className="px-5 py-4 text-left font-bold">Nutzer-Wallet</th>
                            <th className="px-5 py-4 text-left font-bold">Zuletzt aktiv</th>
                            <th className="px-5 py-4 text-right font-bold">Login</th>
                            <th className="px-5 py-4 text-right font-bold">Preview</th>
                            <th className="px-5 py-4 text-right font-bold">Calc</th>
                            <th className="px-5 py-4 text-left font-bold">Analysierte Wallets</th>
                            <th className="px-5 py-4 text-right font-bold">Herkunft</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {sessions.length ? sessions.map((s) => (
                            <tr key={s.user_wallet} className="cursor-pointer hover:bg-blue-50/60" onClick={() => selectWallet(s.user_wallet)}>
                              <td className="px-5 py-4">
                                <div className="font-mono text-sm font-semibold text-slate-900" title={s.user_wallet}>{s.user_wallet}</div>
                              </td>
                              <td className="px-5 py-4 text-slate-600">{formatRelative(s.last_seen_at)}</td>
                              <td className="px-5 py-4 text-right text-lg font-bold">{s.login_count || 0}</td>
                              <td className="px-5 py-4 text-right text-lg font-bold text-blue-700">{s.preview_count || 0}</td>
                              <td className="px-5 py-4 text-right text-lg font-bold text-emerald-700">{s.calculation_count || 0}</td>
                              <td className="px-5 py-4">
                                <div className="max-h-24 space-y-1 overflow-y-auto font-mono text-xs text-slate-700">
                                  {(s.analyzed_wallets || []).length ? (s.analyzed_wallets || []).map((w) => (
                                    <div key={w} className="truncate" title={w}>{w}</div>
                                  )) : <span className="text-slate-400">—</span>}
                                </div>
                              </td>
                              <td className="px-5 py-4 text-right text-slate-600">
                                {s.last_visitor_country ? <CountryBadge code={s.last_visitor_country} /> : "—"}
                              </td>
                            </tr>
                          )) : (
                            <tr><td colSpan={7} className="py-16 text-center text-slate-500">Keine Nutzer im Zeitraum</td></tr>
                          )}
                        </tbody>
                      </table>
                    )}
                  </div>
              </div>

              <button type="button" onClick={() => setChartsOpen((v) => !v)} className="mt-4 flex shrink-0 items-center justify-between rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                Verlauf & Event-Mix (optional)
                {chartsOpen ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
              </button>

              {chartsOpen ? (
                <div className="mt-3 shrink-0 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="grid gap-6 lg:grid-cols-2">
                    <div>
                      <div className="mb-3 text-sm font-bold text-slate-800">Events pro Tag</div>
                      <div className="flex h-32 items-end gap-2">
                        {(data?.byDay || []).map((d) => {
                          const max = Math.max(1, ...(data.byDay || []).map((x) => x.total_events || 0));
                          const h = Math.max(8, Math.round(((d.total_events || 0) / max) * 100));
                          return (
                            <div key={String(d.day)} className="flex flex-1 flex-col items-center justify-end" title={`${d.total_events} Events`}>
                              <div className="w-full max-w-[32px] rounded-t bg-blue-500" style={{ height: `${h}%` }} />
                              <span className="mt-1 text-[10px] text-slate-500">
                                {d.day ? new Date(d.day).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" }) : ""}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className="max-h-36 space-y-2 overflow-y-auto text-sm">
                      {(data?.eventBreakdown || []).map((row) => (
                        <div key={row.event_type} className="flex justify-between gap-3 border-b border-slate-50 pb-1">
                          <span className="text-slate-700">{row.event_type}</span>
                          <span className="font-bold tabular-nums">{row.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
